// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MdAdd, MdAddTask, MdChecklist, MdRefresh } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";

// Internal Components
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import SearchBar from "../UI/SearchBar";
import ListLabels from "../UI/ListLabels";
import LoadingCard from "../UI/LoadingCard";
import PaginationMenu from "../UI/PaginationMenu";
import NoRecordsFound from "../UI/NoRecordsFound";
import AddExpense from "./AddExpense";
import DateFilter from "../UI/DateFilter";
import ExportCsvButton from "../UI/ExportCsvButton";
import FilterMenu from "../UI/FilterMenu";
import ExpenseCard from "./ExpenseCard";
import ExpenseOptions from "./expense-options.json";

/** ========================================================================
 * * Expenses Page
 * Renders a page, showing a list of expenses.
 * ========================================================================== */
export default function Expenses() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    query: "",
    status: "all",
    reconciled: "all",
    category: "",
    paid: "",
    date: "datePaid",
    sort: "datePaid",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(50);
  const [expenses, setExpenses] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [add, setAdd] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "expense",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/expenses?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setExpenses(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Filter Options
  const filterOptions = [
    {
      label: "Date",
      options: [
        { label: "Date Paid", value: "datePaid" },
        { label: "Date Created", value: "dateCreated" },
        { label: "Date Due", value: "dueDate" },
      ],
      selected: filter.date,
    },
    {
      label: "Status",
      options: [
        { label: "All", value: "all" },
        { label: "Pending", value: "pending" },
        { label: "Paid", value: "succeeded" },
      ],
      selected: filter.status,
    },
    {
      label: "Reconciled",
      options: [
        { label: "All", value: "all" },
        { label: "Reconciled", value: "reconciled" },
        { label: "Not Reconciled", value: "not reconciled" },
      ],
      selected: filter.reconciled,
    },
    {
      label: "Category",
      options: [
        ...ExpenseOptions.categories.map((cat) => ({
          label: cat.name,
          value: cat.id,
        })),
      ],
      selected: filter.category,
      type: "multiple",
    },
    {
      label: "Paid",
      options: [
        ...ExpenseOptions.paymentMethods.map((method) => ({
          label: method.name,
          value: method.id,
        })),
        { label: "Unknown", value: "_unknown" },
        { label: "Other", value: "_other" },
      ],
      selected: filter.paid,
      type: "multiple",
    },
  ];

  // Calculate Total
  let total = 0;
  expenses.forEach((expense) => {
    total += Number(expense.amount);
  });

  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container">
      <AddExpense open={add} setOpen={setAdd} onComplete={fetchRecords} />
      <Heading title="Expenses" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdAddTask
          onClick={() => navigate("/payments/report")}
          className="icon"
          title="Report"
        />
        <MdChecklist
          onClick={() => navigate("/transactions")}
          className="icon"
          title="Transactions"
        />
        <MdAdd
          onClick={() => setAdd(true)}
          className="text-3xl icon"
          title="Add Payment"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl icon"
          title="Refresh"
        />
        <ExportCsvButton array={expenses} filename="expenses" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : expenses.length > 0 ? (
        <div className="gap-4 list">
          <div className="lg:grid-cols-9 labels">
            <ListLabels
              labels={[
                { name: "Date Created", key: "dateCreated" },
                { classes: "lg:col-span-3", name: "Description" },
                { name: "Due Date", key: "dueDate" },
                { name: "Date Paid", key: "datePaid" },
                { name: "Category" },
                { classes: "", name: "Association" },
                { classes: "text-right", name: "Amount", key: "amount" },
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {expenses.map((expense, idx) => (
            <ExpenseCard key={idx} expense={expense} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        setLimit={setLimit}
        limit={limit}
        loading={loading}
        count={expenses.length}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            ${total.toFixed(2)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
